/**
 * AlertsRail — left rail showing operational state.
 *  - Severity filter pills
 *  - Alert list (clickable → opens that asset's dossier in the right rail)
 *  - Auto-scrolls; severity dots match map markers
 */
import { useMemo, useState } from 'react';
import { isEmissionsTenant } from '../config/gisConfig';

// Asset-type pills are tenant-specific. The `value` matches the alert's `layer` field.
const LAYER_PILLS_WATER = [
  { value: 'all',       label: 'All' },
  { value: 'disposal',  label: 'Disposal' },
  { value: 'sources',   label: 'Sources' },
  { value: 'pipelines', label: 'Pipelines' },
  { value: 'treatment', label: 'Treatment' },
];
const LAYER_PILLS_EMISSIONS = [
  { value: 'all',       label: 'All' },
  { value: 'leaks',     label: 'Leaks' },
  { value: 'fenceline', label: 'Fenceline' },
];

const SEV_META = {
  critical: { label: 'Critical', color: '#dc2626', bg: '#fef2f2' },
  warning:  { label: 'Warning',  color: '#d97706', bg: '#fffbeb' },
  info:     { label: 'Info',     color: '#2563eb', bg: '#eff6ff' },
};

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AlertsRail({ alerts = [], onAlertClick }) {
  const [severity, setSeverity] = useState('all');
  const [layer, setLayer] = useState('all');

  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    alerts.forEach(a => { if (c[a.severity] != null) c[a.severity]++; });
    return c;
  }, [alerts]);

  const filtered = useMemo(() => {
    return alerts
      .filter(a => severity === 'all' || a.severity === severity)
      .filter(a => layer === 'all' || a.layer === layer)
      .sort((a, b) => {
        const rank = { critical: 0, warning: 1, info: 2 };
        if (rank[a.severity] !== rank[b.severity]) return rank[a.severity] - rank[b.severity];
        return (b.impact_bbl_day || 0) - (a.impact_bbl_day || 0);
      });
  }, [alerts, severity, layer]);

  return (
    <div className="ar-rail">
      <div className="ar-section">
        <div className="ar-section-label">Severity</div>
        <div className="ar-pills">
          <PillButton active={severity === 'all'} onClick={() => setSeverity('all')} label="All" count={alerts.length} />
          <PillButton active={severity === 'critical'} onClick={() => setSeverity('critical')} label="Critical" count={counts.critical} tone="critical" />
          <PillButton active={severity === 'warning'} onClick={() => setSeverity('warning')} label="Warning" count={counts.warning} tone="warning" />
          <PillButton active={severity === 'info'} onClick={() => setSeverity('info')} label="Info" count={counts.info} tone="info" />
        </div>
      </div>

      <div className="ar-section">
        <div className="ar-section-label">{isEmissionsTenant ? 'Source' : 'Asset type'}</div>
        <div className="ar-pills">
          {(isEmissionsTenant ? LAYER_PILLS_EMISSIONS : LAYER_PILLS_WATER).map(p => (
            <PillButton key={p.value} active={layer === p.value} onClick={() => setLayer(p.value)} label={p.label} />
          ))}
        </div>
      </div>

      <div className="ar-section ar-section-list">
        <div className="ar-section-label">
          {filtered.length} alert{filtered.length !== 1 ? 's' : ''}
        </div>
        <div className="ar-list">
          {filtered.length === 0 && (
            <div className="ar-empty">No alerts match the current filter.</div>
          )}
          {filtered.map(a => {
            const sev = SEV_META[a.severity] || SEV_META.info;
            return (
              <button
                key={a.id}
                className="ar-alert"
                onClick={() => onAlertClick && onAlertClick(a)}
                style={{ borderLeftColor: sev.color }}
              >
                <div className="ar-alert-head">
                  <span className="ar-sev-dot" style={{ background: sev.color }} />
                  <span className="ar-asset">{a.asset_name}</span>
                  <span className="ar-time">{fmtRelativeTime(a.first_seen_at)}</span>
                </div>
                <div className="ar-title">{a.title}</div>
                <div className="ar-meta">
                  <span className="ar-county">{a.county}</span>
                  {(a.impact_value || a.impact_bbl_day) > 0 && (
                    <span className="ar-impact">
                      {(a.impact_value || a.impact_bbl_day).toLocaleString()} {a.impact_unit || 'bbl/day'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PillButton({ active, onClick, label, count, tone = 'neutral' }) {
  return (
    <button
      className={`ar-pill ar-pill-${tone} ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      {label}
      {count != null && <span className="ar-pill-count">{count}</span>}
    </button>
  );
}
